import FormatCurrency from 'components/Common/FormatCurrency'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FullWidthCarousel from 'components/Luxkit/Carousel/FullWidthCarousel'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import GlobalSearchContainerPane, { GlobalSearchContainerPaneContent } from 'components/Search/GlobalSearch/GlobalSearchContainerPane'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { fareCabins, fareTypes } from 'constants/flight'
import { FLASH_HOTELS_AND_TAO_OFFERS_AND_BEDBANK_TYPES } from 'constants/offerList'
import GeoContext from 'contexts/geoContext'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import useOfferList from 'hooks/Offers/useOfferList'
import { take, unique, without } from 'lib/array/arrayUtils'
import { mapAirportToAirportLocation, adjustDestinationName } from 'lib/flights/flightUtils'
import { rem } from 'polished'
import React, { useContext, useMemo, useRef } from 'react'
import styled from 'styled-components'
import FlightDealTile from '../FlightMerchandising/FlightDealTile'
import FlightSearchWidget, { FlightSearchWidgetSearchData } from '../FlightsSearch/SearchWidget/FlightSearchWidget'
import FlightDealUSPs from './FlightDealUSPs'
import Typography from 'components/Luxkit/Typography/Typography'
import { useElementHeight } from 'hooks/useElementSize'
import { usePlaceByName } from 'hooks/usePlace'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import SupportTile from 'components/Common/SupportTile/SupportTile'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import uuidV4 from 'lib/string/uuidV4Utils'
import { useAppSelector } from 'hooks/reduxHooks'
import { isMultiCityEnabled } from 'selectors/flightsSelectors'
import Divider from 'components/Luxkit/Divider'
import { FlightDealInfoCard } from './FlightDealInfoCard'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'

const FlightDealHero = styled.div`
  padding-top: ${rem(32)};
  position: relative;
  background-color: ${props => props.theme.palette.neutral.default.one};
  padding-bottom: calc(var(--search-offset, 0) + ${rem(32)});

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(40)};
    padding-bottom: calc(var(--search-offset, 0) + ${rem(40)});
  }
`

const HeroImage = styled(ResponsiveImage)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3));
    pointer-events: none;
  }
`

const Container = styled(Pane)`
  position: relative;
  z-index: 1;
`

const FlightDealSearch = styled(LayoutContainer)`
  transform: translateY(-50%);
`

const ContentSection = styled(Pane)`
  padding: ${rem(40)} 0;
`

const LargeContentSection = styled(Pane)`
  padding: ${rem(56)} 0;
`

const FlightDealInfoCardDesktopContainer = styled.div`
  width: ${rem(360)};
  padding: ${rem(16)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  flex-shrink: 0;
`

interface Props {
  flightDeal: App.FlightDeal;
}

function FlightDealContent(props: Props) {
  const { flightDeal } = props

  let [originFlightDeals] = useFlightDeals('all', { originAirportCode: flightDeal.originAirportCode })
  let [fallbackFlightDeals] = useFlightDeals('all')
  originFlightDeals = without(originFlightDeals, flightDeal)
  fallbackFlightDeals = without(fallbackFlightDeals, flightDeal)
  const otherFlightDeals = useMemo(() => {
    return originFlightDeals.length > 0 ? originFlightDeals : fallbackFlightDeals
  }, [originFlightDeals, fallbackFlightDeals])

  const adjustedDestinationName = adjustDestinationName(flightDeal.destinationName)

  const destinationPlace = usePlaceByName(adjustedDestinationName)
  const multiCityEnabled = useAppSelector(isMultiCityEnabled)

  const alternativeClassFlightDeals = useMemo(() => {
    return otherFlightDeals.filter(deal => deal.originAirportCode === flightDeal.originAirportCode &&
      deal.destinationAirportCode === flightDeal.destinationAirportCode &&
      deal.carrier.code === flightDeal.carrier.code &&
      deal.groupId === flightDeal.groupId,
    )
  }, [flightDeal, otherFlightDeals])

  const [dealFareType, dealClass] = useMemo(() => ([
    fareTypes.find(type => type.value === flightDeal.fareType),
    fareCabins.find(type => type.value === flightDeal.fareClass),
  ]), [flightDeal])

  const { airports } = useContext(GeoContext)

  const searchData = useMemo((): Partial<FlightSearchWidgetSearchData> => {
    const departure = airports.find(airport => airport.code === flightDeal.originAirportCode)
    const arrival = airports.find(airport => airport.code === flightDeal.destinationAirportCode)

    return {
      fareType: dealFareType,
      fareCabin: dealClass,
      filterAirlines: [flightDeal.carrier.code],
      flightDealId: flightDeal.id,
      flights: [{
        id: uuidV4(),
        departureAirport: departure ? mapAirportToAirportLocation(departure) : undefined,
        arrivalAirport: arrival ? mapAirportToAirportLocation(arrival) : undefined,
      }],
      disableCalendarPricing: true,
      dealId: flightDeal.id,
    }
  }, [airports, flightDeal, dealClass, dealFareType])

  const destinationOfferList = useOfferList({
    offerTypes: FLASH_HOTELS_AND_TAO_OFFERS_AND_BEDBANK_TYPES,
    destinationId: destinationPlace?.id,
  }, { disabled: !destinationPlace })
  const isSaleActive = useMemo(() => new Date() < new Date(flightDeal.salesEndDate), [flightDeal.salesEndDate])
  const showBundleHotel = useMemo(() => {
    return isSaleActive && (destinationOfferList.fetching || destinationOfferList.offerIds.length > 0)
  }, [isSaleActive, destinationOfferList])

  const finalOfferList: App.OfferList = useMemo(() => {
    return {
      error: destinationOfferList.error,
      fetching: destinationOfferList.fetching,
      key: 'flightDealOffers',
      offerIds: take(unique(destinationOfferList.offerIds), 16),
    }
  }, [destinationOfferList])

  const containerRef = useRef<HTMLDivElement>(null)
  const searchHeight = useElementHeight(containerRef)

  const searchOffset = searchHeight ? (searchHeight / 2) : undefined

  return <Pane>
    <FlightDealHero style={{ '--search-offset': rem(searchOffset ?? 0) } as any}>
      {!!flightDeal.heroImage?.id && <HeroImage
        id={flightDeal.heroImage.id}
        fit="center"
      />}

      <LayoutContainer style={{ transform: 'translateZ(0)' }}>
        <CSSBreakpoint max="mobile">
          <Group direction="vertical" horizontalAlign="center" gap={16}>
            <VerticalSpacer gap={8}>
              <Heading variant="heading5" align="center" colour="neutral-eight">
                {flightDeal.carrier.name} exclusive offer
              </Heading>

              <Heading variant="heading2" align="center" colour="neutral-eight">
                {flightDeal.originName} to {adjustedDestinationName}{' '}
                from <FormatCurrency value={flightDeal.perAdultPrice} />{' '}
                <Typography as="i" format="lowercase">{dealFareType?.label}*</Typography>
              </Heading>
            </VerticalSpacer>

            {isSaleActive && <OfferUrgencyCountdownLabel
              endDate={flightDeal.salesEndDate}
              tagPlace="flight-deal-content"
            />}
            {!isSaleActive && <UrgencyLabel variant="critical2">
              This sale has ended
            </UrgencyLabel>}
          </Group>
        </CSSBreakpoint>

        <CSSBreakpoint min="tablet">
          <Group direction="horizontal" gap="0 32" verticalAlign="center" horizontalAlign="space-between">
            <Group direction="vertical" gap={16} horizontalAlign="start">
              <VerticalSpacer gap={8}>
                <Heading variant="heading5" colour="neutral-eight">
                  {flightDeal.carrier.name} exclusive offer
                </Heading>

                <Heading variant="heading1" colour="neutral-eight">
                  {flightDeal.originName} to {adjustedDestinationName}{' '}
                  from <FormatCurrency value={flightDeal.perAdultPrice} />{' '}
                  <Typography as="i" format="lowercase">{dealFareType?.label}*</Typography>
                </Heading>
              </VerticalSpacer>

              {isSaleActive && <OfferUrgencyCountdownLabel
                endDate={flightDeal.salesEndDate}
                tagPlace="flight-deal-content"
              />}
              {!isSaleActive && <UrgencyLabel variant="critical2">
                This sale has ended
              </UrgencyLabel>}
            </Group>

            <FlightDealInfoCardDesktopContainer>
              <FlightDealInfoCard deal={flightDeal} alternativeClassFlightDeals={alternativeClassFlightDeals} />
            </FlightDealInfoCardDesktopContainer>
          </Group>
        </CSSBreakpoint>
      </LayoutContainer>
    </FlightDealHero>

    <Container
      style={{ height: searchOffset }}
    >
      <FlightDealSearch ref={containerRef}>
        <GlobalSearchContainerPane displayMobile>
          <GlobalSearchContainerPaneContent>
            <Group direction="vertical" gap="16 0">
              <CSSBreakpoint max="mobile">
                <VerticalSpacer gap={16}>
                  <FlightDealInfoCard deal={flightDeal} alternativeClassFlightDeals={alternativeClassFlightDeals} />
                  <Divider kind="primary" />
                </VerticalSpacer>
              </CSSBreakpoint>

              <FlightSearchWidget
                searchData={searchData}
                showMultiCity={multiCityEnabled}
              />
            </Group>
          </GlobalSearchContainerPaneContent>
        </GlobalSearchContainerPane>
      </FlightDealSearch>
    </Container>

    <ContentSection>
      <LayoutContainer>
        <SupportTile
          title="Call us to find out more or book this offer today"
          description="Our dedicated flights team are available anytime, 24 hours, 7 days a week."
        />
      </LayoutContainer>
    </ContentSection>

    {showBundleHotel && <LargeContentSection>
      <VerticalSpacer gap={32}>
        <LayoutContainer>
          <Heading variant="heading2">
            Bundle hotel + flights to <i>save</i>
          </Heading>
        </LayoutContainer>
        <OfferListCarousel
          overrideOfferList={finalOfferList}
        />
      </VerticalSpacer>
    </LargeContentSection>}
    {otherFlightDeals.length > 0 && <ContentSection>
      <VerticalSpacer gap={32}>
        <LayoutContainer>
          <Heading variant="heading2">
            Explore more <i>limited-time</i> offers {originFlightDeals.length > 0 && 'from ' + flightDeal.originName}
          </Heading>
        </LayoutContainer>
        <FullWidthCarousel gap={20} width="75vw" desktopWidth="320px" snap="start">
          {otherFlightDeals.map((deal, index) => (
            <FlightDealTile key={deal.id} flightDeal={deal} position={index} />
          ))}
        </FullWidthCarousel>
      </VerticalSpacer>
    </ContentSection>}
    <LayoutContainer>
      <ContentSection>
        <VerticalSpacer gap={32}>
          <Heading variant="heading2">
            Why you should <i>book</i> with us
          </Heading>
          <FlightDealUSPs />
        </VerticalSpacer>
      </ContentSection>
      <ContentSection>
        <VerticalSpacer gap={8}>
          <Heading variant="heading6">Terms & Conditions</Heading>
          <BodyText variant="small" colour="neutral-three">
            {flightDeal.termsAndConditions}
          </BodyText>
        </VerticalSpacer>
      </ContentSection>
    </LayoutContainer>
  </Pane>
}

export default FlightDealContent
